import { gzipSync, strToU8 } from 'fflate'
import type { Transport, TransportRequest, TransportResponse } from './transport.js'

/** Compression annoncée dans l'URL (cf. `COMPRESSION_PARAM` de `@belvdr/shared`). */
const GZIP_QUERY = '?compression=gzip'
/**
 * Les navigateurs limitent à 64 Kio le volume des requêtes `keepalive` en cours : au-delà, la
 * requête est refusée. Un batch plus gros part sans `keepalive` (il aboutit si la page survit).
 */
const KEEPALIVE_MAX_BYTES = 60 * 1024
/** Backoff exponentiel : 1 s, 2 s, 4 s… plafonné à 30 s, réduit d'un tirage aléatoire (moitié au plus). */
const RETRY_BASE_MS = 1_000
const RETRY_MAX_MS = 30_000
/**
 * Au-delà de 10 renvois infructueux (environ 3 minutes), les batchs en attente sont abandonnés :
 * la mémoire de la page reste bornée pendant une longue panne.
 */
const MAX_RETRIES = 10
/** Code d'erreur d'une session refusée pour volume (cf. `SESSION_TOO_LARGE` de `@belvdr/shared`). */
const SESSION_TOO_LARGE = 'session_too_large'
/** Code d'erreur d'une page refusée pour son origine (cf. `ORIGIN_NOT_ALLOWED` de `@belvdr/shared`). */
const ORIGIN_NOT_ALLOWED = 'origin_not_allowed'

/**
 * Corps prêt à partir (batch, Exception) : compressé en gzip (synchrone, donc possible jusque dans
 * la fermeture de l'onglet), ou en clair si la compression échoue.
 */
function encode(endpoint: string, body: object): Omit<TransportRequest, 'keepalive'> {
  const json = strToU8(JSON.stringify(body))
  try {
    return { url: endpoint + GZIP_QUERY, body: gzipSync(json) }
  } catch {
    return { url: endpoint, body: json }
  }
}

/** Délai avant la tentative suivante, après `failures` échecs consécutifs (au moins 1). */
function backoffMs(failures: number): number {
  const ceiling = Math.min(RETRY_BASE_MS * 2 ** (failures - 1), RETRY_MAX_MS)
  // Tirage dans [ceiling / 2, ceiling] : les navigateurs ne reviennent pas tous en même temps.
  return ceiling / 2 + (Math.random() * ceiling) / 2
}

export type SendOptions = {
  /** L'onglet passe en arrière-plan ou se ferme : l'envoi doit survivre à la page. */
  keepalive: boolean
  /** Session du batch : ses envois en attente sont abandonnés si l'API la refuse pour volume. */
  sessionId?: string
  /**
   * Fin de l'envoi, une fois pour toutes : la réponse de l'API (réussite ou refus définitif), ou
   * `null` si l'envoi est abandonné (API injoignable trop longtemps, origine refusée…).
   */
  onSettled?: (response: TransportResponse | null) => void
}

export type Sender = {
  send(body: object, options: SendOptions): void
}

type Pending = {
  sessionId: string | undefined
  request: TransportRequest
  onSettled: ((response: TransportResponse | null) => void) | undefined
}

/** Abandonne des envois en attente : chacun l'apprend. */
function abandon(pendings: Pending[]) {
  for (const pending of pendings) pending.onSettled?.(null)
}

/**
 * Envoi fiable des batchs (ou des Exceptions) vers un endpoint. Chaque batch part aussitôt ; si l'API est injoignable, en erreur (5xx)
 * ou limite le débit (429), les envois sont suspendus : le batch en échec est renvoyé seul après
 * un délai (backoff exponentiel, ou `Retry-After`), et les batchs suivants attendent qu'il
 * aboutisse. Un batch refusé (400…) n'est pas renvoyé.
 */
export function createSender(options: {
  endpoint: string
  transport: Transport
  /** L'API refuse la session, devenue trop volumineuse : ses batchs en attente sont abandonnés. */
  onSessionTooLarge?: (sessionId: string) => void
  /**
   * L'API refuse l'origine de la page (absente des origines autorisées du projet) : tous les
   * batchs en attente sont abandonnés, aucun ne serait accepté.
   */
  onOriginNotAllowed: () => void
}): Sender {
  /** Batchs en attente pendant la suspension, dans l'ordre. */
  const queue: Pending[] = []
  /** Envois suspendus (attente d'un délai ou renvoi en cours). */
  let suspended = false
  /** Échecs consécutifs depuis le début de la suspension. */
  let failures = 0

  function dispatch(pending: Pending, probe: boolean) {
    options.transport(pending.request).then(
      (response) => settle(pending, response, probe),
      () => settle(pending, null, probe),
    )
  }

  /** `response` nul : erreur réseau. `probe` : renvoi qui teste si l'API répond à nouveau. */
  function settle(pending: Pending, response: TransportResponse | null, probe: boolean) {
    const status = response?.status
    const retryable = status === undefined || status >= 500 || status === 429
    if (retryable) {
      if (suspended && !probe) {
        // Parti avant la suspension : il attend son tour avec les autres.
        queue.push(pending)
        return
      }
      failures++
      if (failures > MAX_RETRIES) {
        abandon(queue.splice(0))
        abandon([pending])
        resume()
        return
      }
      queue.unshift(pending)
      // 429 : l'API indique quand revenir (limite de débit) ; sinon, backoff exponentiel.
      const retryAfterSec = status === 429 ? response?.retryAfterSec : undefined
      suspend(retryAfterSec === undefined ? backoffMs(failures) : retryAfterSec * 1_000)
      return
    }
    pending.onSettled?.(response)
    const { sessionId } = pending
    if (status === 413 && response?.error === SESSION_TOO_LARGE && sessionId !== undefined) {
      const others = queue.filter((other) => other.sessionId !== sessionId)
      abandon(queue.filter((other) => other.sessionId === sessionId))
      queue.splice(0, queue.length, ...others)
      options.onSessionTooLarge?.(sessionId)
    }
    if (status === 403 && response?.error === ORIGIN_NOT_ALLOWED) {
      abandon(queue.splice(0))
      options.onOriginNotAllowed()
    }
    if (probe) resume()
  }

  function suspend(delayMs: number) {
    suspended = true
    setTimeout(() => {
      const next = queue.shift()
      if (next) dispatch(next, true)
      else resume()
    }, delayMs)
  }

  /** L'API répond : les batchs en attente partent. */
  function resume() {
    suspended = false
    failures = 0
    for (const pending of queue.splice(0)) dispatch(pending, false)
  }

  return {
    send(body, { keepalive, sessionId, onSettled }) {
      const encoded = encode(options.endpoint, body)
      const pending = {
        sessionId,
        onSettled,
        request: { ...encoded, keepalive: keepalive && encoded.body.length <= KEEPALIVE_MAX_BYTES },
      }
      if (suspended) queue.push(pending)
      else dispatch(pending, false)
    },
  }
}
